import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NGORequestForm from './NGORequestForm';
import RequestCard from '../common/RequestCard';
import { auth } from '../../firebase';
import axios from 'axios';
import io from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../../apiConfig';

const IMGBB_API_KEY = '1ff966068ee0b54aa8d6b3cc8b6d492e';

const NGODashboard = ({ user, isLoaded, loadError, MAP_ID }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const afterImageInputRef = useRef(null);
    const [uploadingAfterImage, setUploadingAfterImage] = useState(null);
    const [activeTab, setActiveTab] = useState('Open');

    const fetchNgoRequests = useCallback(async () => {
        setLoading(true);
        try {
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) {
                setLoading(false);
                return;
            }
            const idToken = await firebaseUser.getIdToken();
            const response = await axios.get(`${API_URL}/requests/ngo`, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });
            setRequests(response.data);
        } catch (err) {
            console.error("Error fetching NGO requests:", err);
            setError("Failed to load your requests.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNgoRequests();

        const socket = io(SOCKET_URL);
        socket.on('volunteerCancelled', ({ requestId, volunteerId }) => {
            setRequests(prev => prev.map(req => {
                if (req._id === requestId) {
                    return {
                        ...req,
                        volunteers: req.volunteers.filter(v => v._id !== volunteerId),
                        volunteersAccepted: req.volunteersAccepted - 1,
                    };
                }
                return req;
            }));
        });

        return () => {
            socket.disconnect();
        };
    }, [fetchNgoRequests]);

    const handleNewRequest = (newRequest) => {
        setRequests(prevRequests => [
            newRequest,
            ...prevRequests
        ]);
    };

    const handleVerifyAttendance = async (requestId, volunteerId) => {
        try {
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) throw new Error("User not logged in");
            const idToken = await firebaseUser.getIdToken();

            const response = await axios.post(
                `${API_URL}/requests/${requestId}/verify/${volunteerId}`,
                {},
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            
            setRequests(prevRequests =>
                prevRequests.map(req =>
                    req._id === requestId 
                    ? response.data
                    : req
                )
            );
        } catch (err) {
            console.error("Error verifying attendance:", err);
            alert(err.response?.data?.error || "Could not verify attendance.");
        }
    };

    const handleCompleteRequest = async (requestId) => {
        try {
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) throw new Error("User not logged in");
            const idToken = await firebaseUser.getIdToken();

            await axios.post(
                `${API_URL}/requests/${requestId}/complete`,
                {},
                { headers: { Authorization: `Bearer ${idToken}` } }
            );

            setRequests(prevRequests =>
                prevRequests.map(req =>
                    req._id === requestId ? { ...req, status: 'completed' } : req
                )
            );
        } catch (err) {
            console.error("Error completing request:", err);
            alert(err.response?.data?.error || "Could not complete the request.");
        }
    };

    const handleCancelRequest = async (requestId) => {
        if (window.confirm("Are you sure you want to cancel this request? This action cannot be undone.")) {
            try {
                const firebaseUser = auth.currentUser;
                if (!firebaseUser) throw new Error("User not logged in");
                const idToken = await firebaseUser.getIdToken();

                await axios.post(
                    `${API_URL}/requests/${requestId}/cancel`,
                    {},
                    { headers: { Authorization: `Bearer ${idToken}` } }
                );
                
                setRequests(prevRequests =>
                    prevRequests.map(req =>
                        req._id === requestId ? { ...req, status: 'cancelled' } : req
                    )
                );
            } catch (err) {
                console.error("Error cancelling request:", err);
                alert(err.response?.data?.error || "Could not cancel the request.");
            }
        }
    };
    
    const handleAfterImageUpload = async (requestId, file) => {
        if (!file) return;
        setUploadingAfterImage(requestId);
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("User not logged in");
            const idToken = await user.getIdToken();
            
            const imgbbFormData = new FormData();
            imgbbFormData.append('image', file);
            const imgbbResponse = await axios.post(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, imgbbFormData);
            const afterImageUrl = imgbbResponse.data.data.url;

            const response = await axios.post(
                `${API_URL}/requests/${requestId}/after-image`,
                { afterImageUrl },
                { headers: { Authorization: `Bearer ${idToken}` } }
            );

            setRequests(prev => prev.map(r => r._id === requestId ? response.data : r));

        } catch (err) {
            console.error("Full error object:", err);
            if (err.response) {
                console.error("Error response from server:", err.response.data);
            }
            alert("Failed to upload 'after' image. Please check the console for details.");
        } finally {
            setUploadingAfterImage(null);
        }
    };

    const filteredRequests = useMemo(() => {
        if (activeTab === 'Open') return requests.filter(r => r.status === 'open');
        if (activeTab === 'In Progress') return requests.filter(r => r.status === 'closed');
        if (activeTab === 'Completed') return requests.filter(r => r.status === 'completed' || r.status === 'cancelled');
        return requests;
    }, [requests, activeTab]);

    const stats = useMemo(() => ({
        open: requests.filter(r => r.status === 'open').length,
        inProgress: requests.filter(r => r.status === 'closed').length,
        completed: requests.filter(r => r.status === 'completed').length,
    }), [requests]);

    return (
        <motion.div
            key="ngo-dash"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-4 md:p-6 pt-20"
        >
            <div className="mb-8 text-center">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">
                    Organization Dashboard
                </h1>
                <p className="text-gray-500">Manage your volunteer requests and operations.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Create New Request</h2>
                    <NGORequestForm 
                        onSubmit={handleNewRequest} 
                        isLoaded={isLoaded}
                        loadError={loadError}
                        MAP_ID={MAP_ID}
                    />
                </div>
                
                <div className="lg:col-span-3">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-md border border-white/20 text-center">
                            <p className="text-2xl font-bold text-green-600">{stats.open}</p>
                            <p className="text-sm text-gray-500">Open</p>
                        </div>
                        <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-md border border-white/20 text-center">
                            <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
                            <p className="text-sm text-gray-500">In Progress</p>
                        </div>
                        <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-md border border-white/20 text-center">
                            <p className="text-2xl font-bold text-gray-800">{stats.completed}</p>
                            <p className="text-sm text-gray-500">Completed</p>
                        </div>
                    </div>

                    {/* --- UI UPDATE: Re-added the refresh button --- */}
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex border-b border-gray-200">
                            {['Open', 'In Progress', 'Completed'].map(tab => (
                                <button 
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                                        activeTab === tab ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-black'
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => fetchNgoRequests()} disabled={loading} className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50">
                            <svg className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h5m0 0a9 9 0 11-1.586 5.414M9 9l4.5 4.5" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                         {loading ? (
                            <p>Loading your requests...</p>
                         ) : error ? (
                            <p className="text-red-500">{error}</p>
                         ) : (
                            <AnimatePresence>
                                {filteredRequests.map(request => (
                                    <motion.div 
                                        key={request._id}
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className="bg-white/80 backdrop-blur-lg p-4 rounded-lg shadow-sm border border-white/20"
                                    >
                                        <RequestCard request={request} />
                                        
                                        {request.volunteers && request.volunteers.length > 0 && (
                                            <div className="mt-4 pt-4 border-t">
                                                <h4 className="font-bold text-gray-700 mb-2">Volunteer Roll Call</h4>
                                                <div className="space-y-2">
                                                    {request.volunteers.map(volunteer => (
                                                        <div key={volunteer._id} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                                                            <div>
                                                                <p className="font-semibold text-gray-800">{volunteer.name}</p>
                                                                <p className="text-xs text-gray-500">{volunteer.email}</p>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <span className="text-xs mr-2">{request.attendedVolunteers.includes(volunteer._id) ? 'Verified' : 'Verify'}</span>
                                                                <button
                                                                    onClick={() => handleVerifyAttendance(request._id, volunteer._id)}
                                                                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${request.attendedVolunteers.includes(volunteer._id) ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                                                                >
                                                                    <motion.div layout className="bg-white w-4 h-4 rounded-full shadow-md" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-4 pt-4 border-t flex justify-between items-center">
                                            <span className={`text-sm font-bold uppercase px-2 py-1 rounded ${
                                                request.status === 'open' ? 'bg-green-100 text-green-800' :
                                                request.status === 'closed' ? 'bg-yellow-100 text-yellow-800' :
                                                request.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-200 text-gray-600'
                                            }`}>
                                                {request.status}
                                            </span>
                                            {request.status === 'open' && (
                                                <button onClick={() => handleCancelRequest(request._id)} className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors">
                                                    Cancel Request
                                                </button>
                                            )}
                                            {request.status === 'closed' && (
                                                <button
                                                    onClick={() => handleCompleteRequest(request._id)}
                                                    disabled={request.attendedVolunteers.length === 0}
                                                    className="px-4 py-2 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                >
                                                    Mark as Complete
                                                </button>
                                            )}
                                            {request.status === 'completed' && !request.afterImageUrl && request.beforeImageUrl && (
                                                <>
                                                    <input 
                                                        type="file" 
                                                        ref={el => afterImageInputRef.current = el} 
                                                        onChange={(e) => handleAfterImageUpload(request._id, e.target.files[0])}
                                                        className="hidden" 
                                                        accept="image/*"
                                                    />
                                                    <button 
                                                        onClick={() => afterImageInputRef.current.click()} 
                                                        disabled={uploadingAfterImage === request._id}
                                                        className="px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:bg-blue-300"
                                                    >
                                                        {uploadingAfterImage === request._id ? 'Uploading...' : "Add 'After' Image"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                         )}
                         {!loading && filteredRequests.length === 0 && (
                             <div className="text-center py-10 border-2 border-dashed rounded-lg">
                                 <p className="text-gray-500">No requests in this category.</p>
                             </div>
                         )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default NGODashboard;
